import { Input, InputProps } from '@nextui-org/react'
import {
    Control,
    Controller,
    FieldPath,
    FieldValues,
    Path,
    RegisterOptions
} from 'react-hook-form'

import { Icons } from '../icons'

import { inputValidations } from '@/utils'

import '../../styles/InputControl.css' // Import your CSS file

interface InputControlProps<T extends FieldValues>
    extends Omit<InputProps, 'onChange'> {
    name: Path<T>
    control: Control<T>
    rules?: Omit<
        RegisterOptions<T, FieldPath<T>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >
    onChangeCustom?: (value: string) => void
    allowedPattern?: RegExp
    capitalize?: boolean // New optional prop for capitalization
    forceUppercase?: boolean
    preserveCase?: boolean
    maxWords?: number // New optional prop for max words
    typeInput?: 'text' | 'number'
    passwordInput?: boolean
    showPassword?: () => void
    isPasswordVisible?: boolean
    textSearch?: string
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onSearch?: (value: string) => void
    labelStyle?: React.CSSProperties
    label?: string
}

export const InputControl = <T extends FieldValues>({
    name,
    control,
    rules,
    onChangeCustom,
    allowedPattern = inputValidations.namePattern,
    capitalize = false, // Default to false
    forceUppercase = false,
    preserveCase = false,
    maxWords, // Add maxWords prop
    typeInput = 'text',
    passwordInput = false,
    showPassword,
    textSearch,
    isPasswordVisible,
    onSearch,
    onChange,
    labelStyle,
    label, // Add label to props
    ...props
}: InputControlProps<T>): React.ReactElement => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <div className="input-wrapper">
                    <Input
                        {...field}
                        {...props}
                        variant="bordered"
                        value={field.value || ''}
                        isInvalid={error ? true : false}
                        errorMessage={error ? `${error.message}*` : ''}
                        label={label}
                        type={
                            passwordInput
                                ? isPasswordVisible
                                    ? 'text'
                                    : 'password'
                                : typeInput
                        }
                        className="custom-input"
                        onChange={(e) => {
                            if (onChange) {
                                onChange(e)
                            }
                            let newValue = e.target.value.replace(/\s+/g, ' ') // Trim and replace multiple spaces

                            const shouldUppercase =
                                forceUppercase ||
                                (!preserveCase &&
                                    typeInput === 'text' &&
                                    !passwordInput)

                            if (shouldUppercase) {
                                newValue = newValue.toUpperCase()
                            } else if (capitalize) {
                                newValue = newValue
                                    .split(' ')
                                    .map(
                                        (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1).toLowerCase()
                                    )
                                    .join(' ')
                            }
                            const wordCount = newValue.split('').length

                            if (
                                (!maxWords || wordCount <= maxWords) &&
                                (!allowedPattern ||
                                    allowedPattern.test(newValue))
                            ) {
                                field.onChange(newValue)
                                if (onChangeCustom) {
                                    onChangeCustom(newValue)
                                }
                            }
                        }}
                        onKeyPress={(e) => {
                            if (allowedPattern && !allowedPattern.test(e.key)) {
                                e.preventDefault()
                            }
                        }}
                        onPaste={(e) => {
                            if (passwordInput) {
                                e.preventDefault()
                            } else if (props.onPaste) {
                                props.onPaste(e)
                            }
                        }}
                        endContent={
                            passwordInput && (
                                <button
                                    className="focus:outline-none"
                                    type="button"
                                    onClick={showPassword}
                                    aria-label="toggle password visibility"
                                >
                                    {isPasswordVisible ? (
                                        <Icons.EyeSlash
                                            className={`text-2xl pointer-events-none ${error ? 'text-danger dark:text-danger' : 'text-primary dark:text-primary-dark'} `}
                                        />
                                    ) : (
                                        <Icons.Eye
                                            className={`text-2xl pointer-events-none ${error ? 'text-danger dark:text-danger' : 'text-primary dark:text-primary-dark'}`}
                                        />
                                    )}
                                </button>
                            )
                        }
                    />
                </div>
            )}
        />
    )
}
